import { useCallback, useEffect, useMemo, useRef, useState } from "react";

import {
  deleteReglement,
  getReglements,
} from "../../../../services/achatsServices";

import { useNotification } from "../../../../components/Notification/NotificationManager";

import {
  DEFAULT_FILTERS,
  buildSubscriptionStats,
  extractSubscriptions,
  filterSubscriptions,
  getUniqueFormules,
  paginateItems,
  sortSubscriptions,
} from "../utils/adminSubscriptionsUtils";

export function useAdminSubscriptions() {
  const { addNotification } = useNotification();

  const notificationRef = useRef(addNotification);
  const hasLoadedOnceRef = useRef(false);
  const selectedSubscriptionRef = useRef(null);

  const [subscriptions, setSubscriptions] = useState([]);
  const [filters, setFilters] = useState(DEFAULT_FILTERS);
  const [page, setPage] = useState(1);

  const [selectedSubscription, setSelectedSubscription] = useState(null);
  const [isDrawerOpen, setIsDrawerOpen] = useState(false);

  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);

  useEffect(() => {
    notificationRef.current = addNotification;
  }, [addNotification]);

  useEffect(() => {
    selectedSubscriptionRef.current = selectedSubscription;
  }, [selectedSubscription]);

  const fetchSubscriptions = useCallback(async ({ silent = false } = {}) => {
    const isInitialLoad = !hasLoadedOnceRef.current;

    if (isInitialLoad) {
      setLoading(true);
    } else {
      setRefreshing(true);
    }

    try {
      const data = await getReglements();
      const safeSubscriptions = extractSubscriptions(data);

      setSubscriptions(safeSubscriptions);

      const stats = buildSubscriptionStats(safeSubscriptions);

      if (!silent && !isInitialLoad) {
        notificationRef.current?.("Abonnements actualisés.", "success");
      }

      if (isInitialLoad && stats.alerts > 0) {
        notificationRef.current?.(
          `${stats.alerts} abonnement(s) nécessitent une attention.`,
          "warning",
        );
      }
    } catch (error) {
      console.error("Erreur récupération abonnements :", error);

      notificationRef.current?.(
        "Erreur lors de la récupération des abonnements.",
        "error",
      );
    } finally {
      hasLoadedOnceRef.current = true;
      setLoading(false);
      setRefreshing(false);
    }
  }, []);

  useEffect(() => {
    fetchSubscriptions({ silent: true });
  }, [fetchSubscriptions]);

  const filteredSubscriptions = useMemo(() => {
    return sortSubscriptions(
      filterSubscriptions(subscriptions, filters),
      filters.sort,
    );
  }, [filters, subscriptions]);

  const pagination = useMemo(() => {
    return paginateItems(filteredSubscriptions, page, filters.perPage);
  }, [filteredSubscriptions, filters.perPage, page]);

  const stats = useMemo(() => {
    return buildSubscriptionStats(subscriptions);
  }, [subscriptions]);

  const formuleOptions = useMemo(() => {
    return getUniqueFormules(subscriptions);
  }, [subscriptions]);

  const updateFilter = useCallback((name, value) => {
    setFilters((prev) => ({
      ...prev,
      [name]: value,
    }));

    setPage(1);
  }, []);

  const resetFilters = useCallback(() => {
    setFilters(DEFAULT_FILTERS);
    setPage(1);
  }, []);

  const openSubscriptionDrawer = useCallback((subscription) => {
    setSelectedSubscription(subscription);
    setIsDrawerOpen(true);
  }, []);

  const closeSubscriptionDrawer = useCallback(() => {
    setSelectedSubscription(null);
    setIsDrawerOpen(false);
  }, []);

  const handleDeleteSubscription = useCallback(async (subscriptionId) => {
    const confirmed = window.confirm(
      "Êtes-vous sûr de vouloir supprimer cet achat d’abonnement ?",
    );

    if (!confirmed) return false;

    try {
      await deleteReglement(subscriptionId);

      setSubscriptions((prevSubscriptions) =>
        prevSubscriptions.filter(
          (subscription) => String(subscription.id) !== String(subscriptionId),
        ),
      );

      if (String(selectedSubscriptionRef.current?.id) === String(subscriptionId)) {
        setSelectedSubscription(null);
        setIsDrawerOpen(false);
      }

      notificationRef.current?.("Achat supprimé avec succès.", "success");

      return true;
    } catch (error) {
      console.error("Erreur suppression abonnement :", error);

      notificationRef.current?.(
        "Une erreur est survenue pendant la suppression.",
        "error",
      );

      return false;
    }
  }, []);

  return {
    subscriptions,
    filteredSubscriptions,
    paginatedSubscriptions: pagination.items,
    filters,
    stats,
    formuleOptions,
    selectedSubscription,
    isDrawerOpen,
    loading,
    refreshing,
    page: pagination.page,
    totalPages: pagination.totalPages,
    fetchSubscriptions,
    updateFilter,
    resetFilters,
    setPage,
    openSubscriptionDrawer,
    closeSubscriptionDrawer,
    handleDeleteSubscription,
  };
}